<template>
  <div>
    <validation-observer
      ref="videoSettingsObserver"
      tag="form"
    >
      <div class="px-2 pt-2">
        <b-row class="align-items-start mt-sm-0 mt-n1">
          <b-col
            cols="12"
            order-sm="1"
            order="2"
            sm="6"
          >
            <div class="text-md text-thick mb-2">
              Video - {{ name }}
            </div>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            order-sm="2"
            order="1"
            class="d-flex justify-content-end"
          >
            <b-button
              variant="icon-light"
              @click="$emit('remove')"
            >
              <GjIcon name="Delete" />
              Remove Video
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Video codec"
              class="w-100"
            >
              <input-field
                name="preset_video-codec"
                label="Video codec"
                label-position="top"
                :info="videoInfo.videoCodec"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-codec"
                  v-model="videoDescription.codecType"
                  :options="getOptions(videoDescription.codecTypeOptions)"
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Video Resolution"
              class="w-100"
            >
              <input-field
                name="preset_video-resolution"
                label="Resolution (w x h) - optional"
                label-position="top"
                :info="videoInfo.resolution"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <div class="d-flex w-100 align-items-center">
                  <b-form-input
                    id="preset_video-resolution"
                    v-model="videoDescription.width"
                    type="number"
                    placeholder="Enter width here..."
                  />
                  <span class="mx-1">x</span>
                  <b-form-input
                    v-model="videoDescription.height"
                    type="number"
                    placeholder="Enter height here..."
                  />
                </div>
              </input-field>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex justify-content-end align-items-start"
          >
            <validation-provider
              #default="{ errors }"
              name="Scale Toggle"
              class="w-100"
            >
              <input-field
                name="preset_video-scale"
                label="Scale"
                label-position="right"
                :info="videoInfo.scale"
                info-position="label"
                :error="errors[0]"
                class="mb-0"
                @openInfo="openInfo"
              >
                <b-form-checkbox
                  id="preset_video-scale"
                  v-model="videoDescription.scale"
                  name="scale-toggle"
                  style="height: 2.4rem"
                  switch
                  stacked
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Rate Control Mode"
              class="w-100"
            >
              <input-field
                name="preset_video-rate_control_mode"
                label="Rate control mode"
                label-position="top"
                :info="videoInfo.rateControlMode"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-rate_control_mode"
                  v-model="videoDescription.rateControlMode"
                  :options="getOptions(videoDescription.rateControlModeOptions)"
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Quality Tuning Level"
              class="w-100"
            >
              <input-field
                name="preset_video-quality_tuning_level"
                label="Quality tuning level"
                label-position="top"
                :info="videoInfo.qualityTuningLevel"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-quality_tuning_level"
                  v-model="videoDescription.qualityTuningLevel"
                  :options="
                    getOptions(videoDescription.qualityTuningLevelOptions)
                  "
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Fragmentation Type"
              class="w-100"
            >
              <input-field
                name="preset_video-fragmentationType"
                label="Fragmentation Type"
                label-position="top"
                :error="errors[0]"
              >
                <custom-select
                  id="preset_video-fragmentationType"
                  v-model="videoDescription.fragmentationType"
                  :options="
                    getOptions(videoDescription.fragmentationTypeOptions)
                  "
                />
              </input-field>
              <custom-alert icon-name="Info">
                {{ videoInfo.fragmentationType.message }}
              </custom-alert>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Preset Options"
              class="w-100"
            >
              <input-field
                name="preset_video-presetOption"
                label="Preset Options"
                label-position="top"
                :error="errors[0]"
              >
                <custom-select
                  id="preset_video-presetOption"
                  v-model="videoDescription.presetOption"
                  :options="getOptions(videoDescription.presetOptions)"
                />
              </input-field>
              <custom-alert icon-name="Info">
                {{ videoInfo.presetOption.message }}
              </custom-alert>
            </validation-provider>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Bitrate"
              rules="required|min_value:1000"
            >
              <input-field
                name="preset_video-bitrate"
                label="Bitrate (bit/s)"
                label-position="top"
                :error="errors[0]"
              >
                <div class="w-100">
                  <b-form-input
                    id="preset_video-bitrate"
                    v-model="videoDescription.bitrate"
                    type="number"
                    class="mb-1"
                    placeholder="Enter bitrate here..."
                  />
                  <custom-alert icon-name="Info">
                    {{ videoInfo.bitrate.message }}
                  </custom-alert>
                </div>
              </input-field>
            </validation-provider>
          </b-col>
          <b-col />
        </b-row>
      </div>

      <collapsable title="Framerate">
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="preset_video-resolution"
            >
              <input-field
                name="preset_video-fr_control"
                label="Framerate"
                label-position="top"
                :info="videoInfo.framerate"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-fr_control"
                  v-model="framerateControl"
                  placeholder="Select a framework"
                  :options="getVideoFrameRateOptions"
                  nested-value
                  @change="framerateControlChanged"
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col />
        </b-row>
        <b-row v-if="!isFramerateFollowSource">
          <b-col>
            <validation-provider
              #default="{ errors }"
              rules="required|numeric"
              name="Numerator"
            >
              <input-field
                name="preset_video-framerate_numerator"
                label-position="top"
                :error="errors[0]"
                :has-label="false"
              >
                <b-form-input
                  id="preset_video-framerate_numerator"
                  v-model="videoDescription.framerateNumerator"
                  placeholder="Numerator"
                  @input="setFramerateToCustom"
                />
              </input-field>
            </validation-provider>
          </b-col>

          <span class="mt-1">/</span>
          <b-col>
            <validation-provider
              #default="{ errors }"
              rules="required|numeric|min_value:1"
              name="Denominator"
            >
              <input-field
                name="preset_video-framerate_denominator"
                label-position="top"
                :error="errors[0]"
                :has-label="false"
              >
                <b-form-input
                  id="preset_video-framerate_denominator"
                  v-model="videoDescription.framerateDenominator"
                  placeholder="Denominator"
                  @input="setFramerateToCustom"
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
      </collapsable>
      <collapsable title="Aspect ratio">
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="preset_video-aspect_ratio_control"
            >
              <input-field
                name="preset_video-aspect_ratio_control"
                label="Pixel aspect ratio"
                label-position="top"
                :info="videoInfo.aspectRatio"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-aspect_ratio_control"
                  v-model="aspectRatioControl"
                  placeholder="Select an aspect ratio"
                  :options="getVideoPixelRatioOptions"
                  nested-value
                  @change="aspectRatioChanged"
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col />
        </b-row>
        <b-row v-if="!isAspectRatioFollowSource">
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Denominator"
              rules="required|numeric"
            >
              <input-field
                name="preset_video-aspect_ratio_parnumerator"
                label-position="top"
                :error="errors[0]"
                :has-label="false"
              >
                <b-form-input
                  id="preset_video-aspect_ratio_parnumerator"
                  v-model="videoDescription.parNumerator"
                  placeholder="Par Numerator"
                  @input="setAspectRatioToCustom"
                />
              </input-field>
            </validation-provider>
          </b-col>

          <span class="mt-1">/</span>
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Denominator"
              rules="required|numeric|min_value:1"
            >
              <input-field
                name="preset_video-aspect_ratio_pardenominator"
                label-position="top"
                :error="errors[0]"
                :has-label="false"
              >
                <b-form-input
                  id="preset_video-aspect_ratio_pardenominator"
                  v-model="videoDescription.parDenominator"
                  placeholder="Par Denominator"
                  @input="setAspectRatioToCustom"
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
      </collapsable>
      <collapsable
        title="GOP structure"
        :has-errors="collapsableItems.gop.hasErrors"
      >
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="GOP size"
              rules="required|numeric"
              immediate
            >
              <input-field
                name="preset_video-gop_size"
                label="GOP size"
                label-position="top"
                :info="videoInfo.gopSize"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
                @onErrorChanged="e => (collapsableItems.gop.hasErrors = e)"
              >
                <b-form-input
                  id="preset_video-gop_size"
                  v-model="videoDescription.gopSize"
                  placeholder="Enter GOP size here..."
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Gop Unit"
              class="w-100"
            >
              <input-field
                name="preset_video-gop_unit"
                label="Unit"
                label-position="top"
                :info="videoInfo.gopUnit"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-gop_unit"
                  v-model="videoDescription.gopSizeUnit"
                  :options="getOptions(videoDescription.gopSizeUnitOptions)"
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
        <b-row key="gop-frames">
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              rules="numeric|between:0,7"
              name="B frames"
            >
              <input-field
                name="preset_video-gop_bframes"
                label="B frames between reference frames"
                label-position="top"
                :error="errors[0]"
              >
                <div class="w-100">
                  <b-form-input
                    id="preset_video-bitrate"
                    v-model="
                      videoDescription.numberBFramesBetweenReferenceFrames
                    "
                    placeholder="Enter B frames here..."
                  />
                  <custom-alert
                    items-center
                    class="mt-1"
                    icon-name="Info"
                  >
                    {{ videoInfo.bFrames.message }}
                  </custom-alert>
                </div>
              </input-field>
            </validation-provider>
          </b-col>
          <b-col />
        </b-row>
      </collapsable>
      <collapsable title="Codec Details">
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Profile"
            >
              <input-field
                name="preset_video-codec_details_profile"
                label="Profile"
                label-position="top"
                :info="videoInfo.codecProfile"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-codec_details_profile"
                  v-model="videoDescription.codecProfile"
                  :options="getOptions(videoDescription.codecProfileOptions)"
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Codec Level"
              class="w-100"
            >
              <input-field
                name="preset_video-codec_details_level"
                label="Level"
                label-position="top"
                :info="videoInfo.codecLevel"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-codec_details_level"
                  v-model="videoDescription.codecLevel"
                  :options="getFlatOptions(videoDescription.codecLevelOptions)"
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name="preset_video-color_metadata"
            >
              <input-field
                name="preset_video-color_metadata"
                label="Color metadata"
                label-position="top"
                :info="videoInfo.colorMetadata"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <custom-select
                  id="preset_video-color_metadata"
                  v-model="videoDescription.colorMetadata"
                  :options="getOptions(videoDescription.colorMetadataOptions)"
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col />
        </b-row>
      </collapsable>
      <collapsable
        is-visible
        title="Preprocessors"
      >
        <b-row>
          <b-col
            cols="12"
            sm="6"
          >
            <validation-provider
              #default="{ errors }"
              name=""
            >
              <input-field
                name="preset_video-preprocessors_deinterlace"
                label="Deinterlacer"
                label-position="right"
                :info="videoInfo.deinterlacer"
                info-position="label"
                :error="errors[0]"
                @openInfo="openInfo"
              >
                <b-form-checkbox
                  id="preset_video-preprocessors_deinterlace"
                  v-model="videoDescription.deinterlace"
                  name="check-button"
                  switch
                  stacked
                />
              </input-field>
            </validation-provider>
          </b-col>
          <b-col />
        </b-row>
      </collapsable>
    </validation-observer>
  </div>
</template>

<script>
import Collapsable from '@/components/Collapsable.vue'

import InputField from '@/components/form/InputField.vue'

import helpers from '@/mixins/helpers'

// Vee Rules
import { ValidationProvider, extend, ValidationObserver } from 'vee-validate'

import { min_value, max_value } from 'vee-validate/dist/rules'

import videoInfo from './videoInfo'

const CUSTOM_OPTION = { text: 'Custom', numerator: '', denominator: '' }
const SOURCE_OPTION = {
  text: 'Follow Source',
  numerator: '0',
  denominator: '0',
}

extend('min_value', min_value)
extend('max_value', max_value)

export default {
  name: 'VideoSettings',
  components: {
    ValidationObserver,
    ValidationProvider,
    InputField,
    Collapsable,
  },
  mixins: [helpers],
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      videoInfo,
      collapsableItems: {
        gop: { hasErrors: '' },
      },
      videoDescription: null,
      TEXT: 0,
      NUMERATOR: 1,
      DENOMINATOR: 2,
      aspectRatioControl: {
        text: 'Follow Source',
        numerator: '0',
        denominator: '0',
      },
      framerateControl: {
        text: 'Follow Source',
        numerator: '0',
        denominator: '0',
      },
    }
  },
  computed: {
    isFramerateFollowSource() {
      return this.framerateControl.text === SOURCE_OPTION.text
    },
    isAspectRatioFollowSource() {
      return this.aspectRatioControl.text === SOURCE_OPTION.text
    },
    name() {
      return this.videoDescription.codecTypeOptions.find(
        cto => cto.id === this.videoDescription.codecType,
      ).value
    },
    getVideoFrameRateOptions() {
      return this.videoDescription.videoFrameRateOptions.map(fr => ({
        text: fr[this.TEXT],
        value: {
          text: fr[this.TEXT],
          numerator: fr[this.NUMERATOR],
          denominator: fr[this.DENOMINATOR],
        },
      }))
    },
    getVideoPixelRatioOptions() {
      return this.videoDescription.videoPixelRatioOptions.map(fr => ({
        text: fr[this.TEXT],
        value: {
          text: fr[this.TEXT],
          numerator: fr[this.NUMERATOR],
          denominator: fr[this.DENOMINATOR],
        },
      }))
    },
  },
  created() {
    this.init()
  },

  methods: {
    init() {
      const emptyModel = {
        ...this.$store.state.outputPresets.emptyOutputPreset.videoDescription,
      }
      if (this.edit) {
        this.videoDescription = {
          ...(this.$store.state.outputPresets?.outputPreset?.videoDescription
            || emptyModel),
        }
      } else {
        this.videoDescription = { ...emptyModel }
      }
      this.initFrameRateControl()
      this.initAspectRatioControl()
    },
    initFrameRateControl() {
      if (this.edit) {
        const n = this.videoDescription.framerateNumerator
        const d = this.videoDescription.framerateDenominator
        const findControl = this.getVideoFrameRateOptions.find(
          op => op.value.numerator === `${n}` && op.value.denominator === `${d}`,
        )
        if (findControl) {
          this.framerateControl = findControl.value
        } else if (n === '0' || d === '0') {
          this.framerateControl = SOURCE_OPTION
        } else {
          this.framerateControl = CUSTOM_OPTION
        }
      } else {
        this.framerateControl = SOURCE_OPTION
      }
    },
    initAspectRatioControl() {
      if (this.edit) {
        const n = this.videoDescription.parNumerator
        const d = this.videoDescription.parDenominator
        const findControl = this.getVideoPixelRatioOptions.find(op => {
          const temp = op.value.numerator === `${n}` && op.value.denominator === `${d}`
          return temp
        })
        if (findControl) {
          this.aspectRatioControl = findControl.value
        } else if (n === '0' || d === '0') {
          this.aspectRatioControl = SOURCE_OPTION
        } else {
          this.aspectRatioControl = CUSTOM_OPTION
        }
      } else {
        this.aspectRatioControl = SOURCE_OPTION
      }
    },
    isCustom(opt) {
      return opt.text === CUSTOM_OPTION.text
    },
    framerateControlChanged(e) {
      this.videoDescription.framerateDenominator = e.denominator
      this.videoDescription.framerateNumerator = e.numerator
    },
    aspectRatioChanged(e) {
      this.videoDescription.parDenominator = e.denominator
      this.videoDescription.parNumerator = e.numerator
    },
    setFramerateToCustom() {
      if (!this.isCustom(this.framerateControl)) {
        this.framerateControl = CUSTOM_OPTION
      }
    },
    setAspectRatioToCustom() {
      if (!this.isCustom(this.aspectRatioControl)) {
        this.aspectRatioControl = CUSTOM_OPTION
      }
    },
    openInfo(e) {
      this.$emit('openInfo', e)
    },
  },
}
</script>
<style scoped>

</style>
